export const DECIDE_PX = 4;
export const GIVE_UP_PX = 16;
export const PULL_SLOP = 8;
export const DRAG_RATE = 0.5;
export const TRIGGER = 64;
export const SLINGSHOT = 64;

export interface PullGestureHandlers {
  canStart: () => boolean;
  onStart: () => void;
  onMove: (offset: number, armed: boolean) => void;
  onEnd: (armed: boolean) => void;
  onCancel: () => void;
}

type GestureState = "idle" | "pending" | "pulling" | "ignored";

const PASSIVE: AddEventListenerOptions = { passive: true };
const NON_PASSIVE: AddEventListenerOptions = { passive: false };
const OPEN_OVERLAY = '[aria-modal="true"], [role="listbox"], [role="menu"]';
const IGNORED_TARGET =
  '[data-ptr-ignore], [role="dialog"], [role="listbox"], [role="menu"], textarea, select, [contenteditable="true"]';
const NON_TEXT_INPUTS = [
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit"
];
const KEYBOARD_EVENTS: [string, boolean][] = [
  ["keyboardWillShow", true],
  ["keyboardDidShow", true],
  ["keyboardWillHide", false],
  ["keyboardDidHide", false]
];

let keyboardVisible: boolean | null = null;
let isTrackingKeyboard = false;

function trackKeyboard() {
  if (isTrackingKeyboard) return;
  isTrackingKeyboard = true;
  KEYBOARD_EVENTS.forEach(([name, visible]) =>
    window.addEventListener(name, () => {
      keyboardVisible = visible;
    })
  );
}

function isTextEntry(element: Element | null) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable || element.tagName === "TEXTAREA" || element.tagName === "SELECT")
    return true;
  return (
    element.tagName === "INPUT" && !NON_TEXT_INPUTS.includes((element as HTMLInputElement).type)
  );
}

const isScrollable = (overflow: string) => overflow === "auto" || overflow === "scroll";

export function pullOffset(distance: number) {
  const over = Math.max(0, distance) * DRAG_RATE;
  if (over <= TRIGGER) return over;
  const x = Math.min(over - TRIGGER, SLINGSHOT * 2) / SLINGSHOT;
  const tension = (x / 4 - Math.pow(x / 4, 2)) * 2;
  return TRIGGER + SLINGSHOT * tension * 2;
}

function surfaceUnder(target: EventTarget | null, root: HTMLElement) {
  if (
    document.querySelector(OPEN_OVERLAY) ||
    !(target instanceof Element) ||
    !root.contains(target)
  )
    return "blocked";
  let isHorizontal = false;
  for (
    let element: Element | null = target;
    element && element !== root;
    element = element.parentElement
  ) {
    if (element.matches(IGNORED_TARGET)) return "blocked";
    const style = getComputedStyle(element);
    if (
      isScrollable(style.overflowY) &&
      element.scrollHeight > element.clientHeight + 1 &&
      (element.scrollTop > 0 || /contain|none/.test(style.overscrollBehaviorY ?? ""))
    )
      return "blocked";
    if (
      element.classList.contains("swiper") ||
      (isScrollable(style.overflowX) && element.scrollWidth > element.clientWidth + 1)
    )
      isHorizontal = true;
  }
  return isHorizontal ? "horizontal" : "free";
}

export function attachPullToRefreshGesture(root: HTMLElement, handlers: PullGestureHandlers) {
  let state: GestureState = "idle";
  let touchId = -1;
  let startX = 0;
  let startY = 0;
  let target: EventTarget | null = null;
  let offset = 0;
  trackKeyboard();

  const findTouch = (list: TouchList) => {
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].identifier === touchId) return list[i];
    }
    return undefined;
  };

  const cancel = (next: GestureState) => {
    if (state === "pulling") handlers.onCancel();
    state = next;
  };

  const whenDetached = (handler: (event: TouchEvent) => void) => (event: TouchEvent) => {
    if (target instanceof Node && !root.contains(target)) handler(event);
  };

  const onTouchStart = (event: TouchEvent) => {
    watchTarget(null);
    if (event.touches.length !== 1) {
      cancel("ignored");
      return;
    }
    cancel("idle");
    if (!handlers.canStart() || root.scrollTop > 0 || event.defaultPrevented) {
      state = "ignored";
      return;
    }
    const touch = event.touches[0];
    touchId = touch.identifier;
    startX = touch.clientX;
    startY = touch.clientY;
    watchTarget(event.target);
    offset = 0;
    state = "pending";
  };

  const onTouchMove = (event: TouchEvent) => {
    if (state !== "pending" && state !== "pulling") return;
    if (event.touches.length !== 1) {
      cancel("ignored");
      return;
    }
    const touch = findTouch(event.touches);
    if (!touch) return;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    if (state === "pending") {
      if (Math.abs(dx) < DECIDE_PX && Math.abs(dy) < DECIDE_PX) return;
      if (!event.cancelable) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) >= GIVE_UP_PX) state = "ignored";
        return;
      }
      const surface =
        dy > 0 &&
        !event.defaultPrevented &&
        root.scrollTop <= 0 &&
        !(keyboardVisible ?? isTextEntry(document.activeElement))
          ? surfaceUnder(target, root)
          : "blocked";
      if (surface === "blocked" || dy <= Math.abs(dx) * (surface === "horizontal" ? 2 : 1)) {
        state = "ignored";
        return;
      }
      state = "pulling";
      handlers.onStart();
    }
    if (event.cancelable) event.preventDefault();
    offset = pullOffset(dy - PULL_SLOP);
    handlers.onMove(offset, offset >= TRIGGER);
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (state === "pulling" && findTouch(event.changedTouches)) {
      state = event.touches.length ? "ignored" : "idle";
      handlers.onEnd(offset >= TRIGGER);
      return;
    }
    if (!event.touches.length) state = "idle";
  };

  const onTouchCancel = (event: TouchEvent) => cancel(event.touches.length ? "ignored" : "idle");

  const onDetachedMove = whenDetached(onTouchMove);
  const onDetachedEnd = whenDetached(onTouchEnd);
  const onDetachedCancel = whenDetached(onTouchCancel);

  function watchTarget(next: EventTarget | null) {
    target?.removeEventListener("touchmove", onDetachedMove as EventListener, NON_PASSIVE);
    target?.removeEventListener("touchend", onDetachedEnd as EventListener, PASSIVE);
    target?.removeEventListener("touchcancel", onDetachedCancel as EventListener, PASSIVE);
    target = next;
    target?.addEventListener("touchmove", onDetachedMove as EventListener, NON_PASSIVE);
    target?.addEventListener("touchend", onDetachedEnd as EventListener, PASSIVE);
    target?.addEventListener("touchcancel", onDetachedCancel as EventListener, PASSIVE);
  }

  root.addEventListener("touchstart", onTouchStart, PASSIVE);
  root.addEventListener("touchmove", onTouchMove, NON_PASSIVE);
  root.addEventListener("touchend", onTouchEnd, PASSIVE);
  root.addEventListener("touchcancel", onTouchCancel, PASSIVE);

  return () => {
    root.removeEventListener("touchstart", onTouchStart, PASSIVE);
    root.removeEventListener("touchmove", onTouchMove, NON_PASSIVE);
    root.removeEventListener("touchend", onTouchEnd, PASSIVE);
    root.removeEventListener("touchcancel", onTouchCancel, PASSIVE);
    watchTarget(null);
    cancel("idle");
  };
}
